import EvaluationReportModal, {
  loadEvaluations,
  loadOwnProsecutors,
  NormalizedEval,
  onlyOwnProsecutors,
} from "components/EvaluationReportModal";
import { Award } from "lucide-react";
import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

const RECENT = "recent";

interface Column {
  key: string;
  label: string;
  from: Date;
  to: Date;
}

const monthsAgo = (months: number) => {
  const d = new Date();
  d.setMonth(d.getMonth() - months);
  return d;
};

// Average overall score and evaluation count for one prosecutor (or the whole
// team when userId is empty) over [from, to).
const scoreOf = (evals: NormalizedEval[], col: Column, userId?: string) => {
  const hits = evals.filter((e) => {
    const d = new Date(e.date);
    if (isNaN(d.getTime()) || d < col.from || d >= col.to) return false;
    return userId ? e.members.some((m) => m.userId === userId) : true;
  });
  if (!hits.length) return null;
  return {
    count: hits.length,
    overall: Math.round(hits.reduce((sum, e) => sum + e.overall, 0) / hits.length),
  };
};

const scoreColor = (v: number) =>
  v >= 80 ? "text-green-700 bg-green-50" : v >= 60 ? "text-amber-700 bg-amber-50" : "text-red-700 bg-red-50";

/**
 * Dashboard card for directors and coordinators: the team evaluation scores
 * of their own prosecutors (the prosecutors of their unit) for the last 3, 6
 * and 12 months, or for a past year by quarter, half year and whole year.
 */
const EvaluationSummary: React.FC<{ user: any; bearer: string; className?: string }> = ({
  user,
  bearer,
  className = "",
}) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [evaluations, setEvaluations] = useState<NormalizedEval[]>([]);
  // Prosecutors of the user's unit (id -> name); null when the list could not be read.
  const [ownProsecutors, setOwnProsecutors] = useState<Map<string, string> | null>(null);
  const [view, setView] = useState<string>(RECENT);
  const [showReport, setShowReport] = useState(false);

  useEffect(() => {
    if (!bearer) return;
    let cancelled = false;
    setLoading(true);
    setFailed(false);
    Promise.all([loadEvaluations(bearer, t), loadOwnProsecutors(bearer, user)])
      .then(([{ evaluations: evals }, own]) => {
        if (cancelled) return;
        setEvaluations(evals);
        setOwnProsecutors(own);
      })
      .catch(() => !cancelled && setFailed(true))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
    // t only changes with the language; the labels are re-read on render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bearer, user?.admin_unit, user?.org_unit]);

  // Only the user's own prosecutors (all evaluated prosecutors if the unit list could not be read).
  const ownEvals = useMemo(() => onlyOwnProsecutors(evaluations, ownProsecutors), [evaluations, ownProsecutors]);

  const currentYear = new Date().getFullYear();
  const years = useMemo(() => {
    const found = new Set<number>([currentYear]);
    ownEvals.forEach((e) => {
      const y = new Date(e.date).getFullYear();
      if (!isNaN(y)) found.add(y);
    });
    return Array.from(found).sort((a, b) => b - a);
  }, [ownEvals, currentYear]);

  const columns: Column[] = useMemo(() => {
    if (view === RECENT) {
      const now = new Date();
      now.setDate(now.getDate() + 1);
      return [
        { key: "3", label: t("rd_last_3_months"), from: monthsAgo(3), to: now },
        { key: "6", label: t("rd_last_6_months"), from: monthsAgo(6), to: now },
        { key: "12", label: t("rd_last_12_months"), from: monthsAgo(12), to: now },
      ];
    }
    const y = Number(view);
    const at = (month: number) => new Date(y, month, 1);
    return [
      ...[1, 2, 3, 4].map((n) => ({
        key: `q${n}`,
        label: t("rd_quarter_n", { n }),
        from: at((n - 1) * 3),
        to: at(n * 3),
      })),
      ...[1, 2].map((n) => ({
        key: `h${n}`,
        label: t("rd_half_n", { n }),
        from: at((n - 1) * 6),
        to: at(n * 6),
      })),
      { key: "year", label: t("rd_whole_year"), from: at(0), to: at(12) },
    ];
  }, [view, t]);

  const rows = useMemo(() => {
    const people = new Map<string, string>();
    ownEvals.forEach((e) =>
      e.members.forEach((m) => {
        if (m.userId) people.set(m.userId, m.name);
      }),
    );
    const last = columns[columns.length - 1];
    return Array.from(people.entries())
      .map(([userId, name]) => ({
        userId,
        name,
        cells: columns.map((col) => scoreOf(ownEvals, col, userId)),
      }))
      .filter((row) => row.cells.some(Boolean))
      .sort(
        (a, b) =>
          (scoreOf(ownEvals, last, b.userId)?.overall ?? -1) - (scoreOf(ownEvals, last, a.userId)?.overall ?? -1) ||
          a.name.localeCompare(b.name),
      );
  }, [ownEvals, columns]);

  const teamCells = columns.map((col) => scoreOf(ownEvals, col));

  const cell = (score: { count: number; overall: number } | null, key: string, bold = false) => (
    <td key={key} className="px-2 py-1.5 text-center">
      {score ? (
        <span
          className={`inline-flex items-baseline gap-0.5 rounded px-1.5 py-0.5 ${scoreColor(score.overall)} ${
            bold ? "font-bold" : "font-semibold"
          }`}
          title={t("rd_evaluations_count", { count: score.count })}
        >
          {score.overall}%<span className="text-[10px] font-normal opacity-70">({score.count})</span>
        </span>
      ) : (
        <span className="text-gray-300">—</span>
      )}
    </td>
  );

  return (
    <section className={`flex flex-col rounded-xl border border-gray-200 bg-white p-5 shadow ${className}`}>
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <h2
          className="flex items-center gap-2 text-base font-semibold text-gray-900"
          title={t("rd_eval_summary_hint")}
        >
          <Award size={18} className="text-[#AA232F]" />
          {t("rd_eval_summary")}
        </h2>
        <div className="flex items-center gap-1.5">
          <select
            value={view}
            onChange={(e) => setView(e.target.value)}
            style={{ width: "auto" }}
            className="cursor-pointer rounded-md border border-gray-200 bg-white py-1 pl-2 pr-6 text-xs font-medium text-gray-700 focus:border-[#AA232F] focus:outline-none focus:ring-0"
          >
            <option value={RECENT}>{t("rd_recent_periods")}</option>
            {years.map((y) => (
              <option key={y} value={String(y)}>
                {t("rd_year_label", { year: y })}
              </option>
            ))}
          </select>
          <button
            onClick={() => setShowReport(true)}
            className="whitespace-nowrap rounded-md px-2 py-1 text-xs font-medium text-[#AA232F] hover:bg-[#FDEBEC]"
          >
            {t("rd_full_report")} →
          </button>
        </div>
      </div>

      {loading ? (
        <p className="py-6 text-center text-sm text-gray-500">{t("rd_loading")}</p>
      ) : failed ? (
        <p className="py-6 text-center text-sm text-gray-500">{t("rd_load_failed")}</p>
      ) : !rows.length ? (
        <p className="py-6 text-center text-sm text-gray-500">{t("rd_no_evaluations")}</p>
      ) : (
        <div className="max-h-72 overflow-auto rounded-lg border border-gray-100">
          <table className="w-full text-xs">
            <thead className="sticky top-0 z-10 bg-gray-50 text-gray-500">
              <tr>
                <th className="px-3 py-2 text-left font-medium">{t("rd_prosecutor")}</th>
                {columns.map((col) => (
                  <th key={col.key} className="whitespace-nowrap px-2 py-2 text-center font-medium">
                    {col.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.userId} className="border-t border-gray-100 hover:bg-gray-50">
                  <td className="max-w-[12rem] truncate px-3 py-1.5 text-gray-800" title={row.name}>
                    {row.name}
                  </td>
                  {row.cells.map((score, i) => cell(score, columns[i].key))}
                </tr>
              ))}
            </tbody>
            <tfoot className="sticky bottom-0 bg-gray-50">
              <tr className="border-t border-gray-200">
                <td className="px-3 py-1.5 font-semibold text-gray-900">{t("rd_team_average")}</td>
                {teamCells.map((score, i) => cell(score, columns[i].key, true))}
              </tr>
            </tfoot>
          </table>
        </div>
      )}

      {showReport && <EvaluationReportModal bearer={bearer} onClose={() => setShowReport(false)} />}
    </section>
  );
};

export default EvaluationSummary;
